import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from './LanguageContext';
import {
  ArrowLeft,
  Menu, X, ChevronDown, ChevronRight, Maximize2, Minimize2, Circle,
  Home, Settings, Users, DollarSign, Briefcase, Package, Building2, Zap,
  Activity, Clock, User, Target, TrendingUp, Heart, Smile, ShoppingCart,
  Wrench, Truck, Box, AlertTriangle, Eye, FileText, Brain, Database, BookOpen,
  Code, HelpCircle, Book, TrendingDown, Wallet, ArrowRightLeft, ContactRound, ShieldCheck,
  MessageSquare, Network, ClipboardList, LayoutDashboard, BrainCircuit, Library, Bot, ScrollText
} from 'lucide-react';
import menuData from './menuStructure.json';
import Header from './Header';
import { ModuleIcon, modulePresentation } from './modulePresentation';
import { getSidebarMenuGroups, resolveActiveMenuLocation } from './sidebarMenu';
import { useAuth } from './AuthContext';
import { hasPermission } from './accessControl';
import { buildDashboardReturnPath, getDashboardReturnContext } from './dashboardNavigation';
import { useModuleNavigation } from './moduleNavigation';
import { getProductionServiceReturnPath } from './productionServiceNavigation';

// Mapping des icônes
const iconMap = {
  Home, Settings, Users, DollarSign, Briefcase, Package, Building2, Zap,
  Activity, Clock, User, Target, TrendingUp, Heart, Smile, ShoppingCart,
  Wrench, Truck, Box, AlertTriangle, Eye, FileText, Brain, Database, BookOpen,
  Code, HelpCircle, Book, TrendingDown, Wallet, ArrowRightLeft, ContactRound, ShieldCheck,
  MessageSquare, Network, ClipboardList, LayoutDashboard, BrainCircuit, Library, Bot, ScrollText
};

const sidebarGroups = getSidebarMenuGroups(menuData);

const Layout = ({ children }) => {
  const mainRef = useRef(null);
  const returnNavRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  const canViewChild = child => hasPermission(user?.permissions, child?.requiredPermission);
  const [sidebarOpen, setSidebarOpen] = useState(() => (
    typeof window === 'undefined' || window.innerWidth >= 1024
  ));
  const [expandedMenus, setExpandedMenus] = useState({});
  const [expandAll, setExpandAll] = useState(false);
  const activeMenu = resolveActiveMenuLocation(menuData, location.pathname, location.search);
  const activeParentId = activeMenu.parent?.id;
  const activeParentHasChildren = Boolean(activeMenu.parent?.children?.length);
  const dashboardReturn = getDashboardReturnContext(location.search);
  const serviceReturnPath = getProductionServiceReturnPath(location.pathname, location.search);
  useModuleNavigation(mainRef, location);

  // Traductions UI
  const translations = {
    FR: {
      logout: 'Déconnexion',
      expandAll: 'Déplier tout',
      collapseAll: 'Replier tout',
      backToDashboard: 'Retour à l’indicateur du Tableau de bord',
      backToDashboardSection: 'Retour à la section du Tableau de bord',
      backToServices: 'Retour au suivi des prestations'
    },
    EN: {
      logout: 'Logout',
      expandAll: 'Expand All',
      collapseAll: 'Collapse All',
      backToDashboard: 'Back to the Dashboard indicator',
      backToDashboardSection: 'Back to the Dashboard section',
      backToServices: 'Back to service delivery follow-up'
    },
    DE: {
      logout: 'Abmelden',
      expandAll: 'Alles erweitern',
      collapseAll: 'Alles einklappen',
      backToDashboard: 'Zur Dashboard-Kennzahl zurück',
      backToDashboardSection: 'Zum Dashboard-Bereich zurück',
      backToServices: 'Zurück zur Leistungsverfolgung'
    }
  };

  const t = translations[language];
  const returnPath = serviceReturnPath || (dashboardReturn.enabled
    ? buildDashboardReturnPath(dashboardReturn.indicatorId, dashboardReturn.view, dashboardReturn.sectionId)
    : null);
  const returnLabel = serviceReturnPath ? t.backToServices
    : dashboardReturn.indicatorId ? t.backToDashboard : t.backToDashboardSection;

  useEffect(() => {
    const main = mainRef.current;
    const nav = returnNavRef.current;
    const measure = () => main?.style.setProperty('--m3s-subnav-offset', `${nav?.getBoundingClientRect().height || 0}px`);
    measure();
    if (!nav || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(nav);
    return () => observer.disconnect();
  }, [dashboardReturn.enabled, serviceReturnPath]);

  useEffect(() => {
    if (!activeParentHasChildren) return;
    setExpandedMenus(previous => ({ ...previous, [activeParentId]: true }));
  }, [activeParentHasChildren, activeParentId]);

  // Expand/Collapse All
  const toggleExpandAll = () => {
    const newExpandAll = !expandAll;
    setExpandAll(newExpandAll);

    const newState = {};
    menuData.menu.forEach(item => {
      if (item.children && item.children.length > 0) {
        newState[item.id] = newExpandAll;
      }
    });
    setExpandedMenus(newState);
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  return (
    <div className="app-shell flex h-screen overflow-hidden bg-slate-900 text-gray-100">
      {sidebarOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-slate-950/60 backdrop-blur-[1px] lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-label="Fermer le menu"
        />
      )}
      {/* Sidebar */}
      <div className={`${
        sidebarOpen
          ? 'w-72 max-lg:translate-x-0'
          : 'w-20 max-lg:-translate-x-full'
      } max-lg:fixed max-lg:inset-y-0 max-lg:left-0 max-lg:z-50 max-lg:w-72 bg-slate-800 border-r border-slate-700 transition-all duration-300 flex flex-col overflow-hidden`}>

        {/* Header */}
        <div className="h-20 px-4 border-b border-slate-700 flex items-center justify-between">
          {sidebarOpen && (
            <div
              className="flex flex-col space-y-1 items-center text-center flex-1"
              style={{ fontFamily: '"Segoe UI", Inter, Arial, sans-serif' }}
            >
              <div className="flex items-center space-x-2 justify-center">
                {/* Logo Image */}
                <img src="/assets/logo-2sg.png" alt="Logo SeneSwiss Group" className="w-11 h-11 rounded-full object-cover flex-shrink-0 bg-white shadow-md" />
                <h1 className="text-base font-semibold text-blue-400 whitespace-nowrap">2SG - SeneSwiss Group</h1>
              </div>
              <p className="text-xs text-slate-500 whitespace-nowrap overflow-hidden text-ellipsis">M3S v2.0 - Management System - SeneSwiss</p>
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="sidebar-toggle-button p-2 hover:bg-slate-700 rounded flex-shrink-0"
            title={sidebarOpen ? "Masquer menu" : "Afficher menu"}
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Expand All Button */}
        {sidebarOpen && (
          <div className="p-4 pt-3 border-b border-slate-700">
            <button
              onClick={toggleExpandAll}
              className="sidebar-expand-button w-full flex items-center justify-center space-x-2 px-3 py-2 text-xs bg-slate-700 hover:bg-slate-600 rounded transition"
              title={expandAll ? t.collapseAll : t.expandAll}
            >
              {expandAll ? (
                <>
                  <Minimize2 size={14} />
                  <span>{t.collapseAll}</span>
                </>
              ) : (
                <>
                  <Maximize2 size={14} />
                  <span>{t.expandAll}</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-2" aria-label="Navigation M3S">
          {sidebarGroups.map((group, groupIndex) => (
            <section
              key={group.id}
              aria-labelledby={sidebarOpen ? `sidebar-group-${group.id}` : undefined}
              className={groupIndex > 0 ? 'mt-3 border-t border-slate-700/80 pt-3' : ''}
            >
              {sidebarOpen && group.label?.FR && (
                <p
                  id={`sidebar-group-${group.id}`}
                  className="sidebar-group-label px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500"
                >
                  {group.label[language] || group.label.FR}
                </p>
              )}
              <div className="space-y-1">
                {group.items.map(item => (
                  <div key={item.id}>
                    {(() => {
                      const parentActive = activeMenu.parent?.id === item.id;
                      return (
                    <button
                      onClick={() => {
                        const hasChildren = item.children && item.children.some(canViewChild);
                        const isExpanded = Boolean(expandedMenus[item.id]);

                        handleMenuItemClick(item.path);

                        if (sidebarOpen && hasChildren) {
                          setExpandedMenus(isExpanded ? {} : { [item.id]: true });
                        } else {
                          setExpandedMenus({});
                        }
                      }}
                      title={!sidebarOpen ? (item.label[language] || item.label.FR) : undefined}
                      aria-current={parentActive && !activeMenu.child ? 'page' : undefined}
                      className={`sidebar-nav-item ${parentActive ? 'sidebar-nav-item--active' : 'sidebar-nav-item--inactive'} flex items-center rounded hover:bg-slate-700 transition text-left text-sm cursor-pointer ${
                        sidebarOpen
                          ? 'w-full space-x-3 px-4 py-2'
                          : 'w-full justify-center py-3'
                      } ${parentActive ? 'bg-slate-700 text-white shadow-sm ring-1 ring-inset ring-slate-600' : 'text-slate-200'}`}
                    >
                      <div className="flex-shrink-0 flex items-center space-x-2">
                        <div className={`w-8 h-8 flex items-center justify-center rounded-md ${modulePresentation[item.id]?.bg || 'bg-slate-700'}`}>
                          <ModuleIcon moduleId={item.id} size={18} />
                        </div>

                        {sidebarOpen && item.children && item.children.some(canViewChild) && (
                          <div className="flex-shrink-0">
                            {expandedMenus[item.id] ? (
                              <ChevronDown size={16} className="text-blue-400" />
                            ) : (
                              <ChevronRight size={16} className="text-slate-400" />
                            )}
                          </div>
                        )}
                      </div>

                      {sidebarOpen && (
                        <span className="flex-1 font-medium">
                          {item.label[language] || item.label.FR}
                        </span>
                      )}
                    </button>
                      );
                    })()}

                    {sidebarOpen && expandedMenus[item.id] && item.children && item.children.some(canViewChild) && (
                      <div className="sidebar-submenu ml-6 space-y-1 bg-slate-700 bg-opacity-30 rounded my-1 py-1 px-2">
                        {item.children.filter(canViewChild).map(child => {
                          const childActive = activeMenu.child?.id === child.id;
                          return (
                          <button
                            key={child.id}
                            onClick={() => handleMenuItemClick(child.path)}
                            aria-current={childActive ? 'page' : undefined}
                            className={`sidebar-submenu-item ${childActive ? 'sidebar-submenu-item--active' : 'sidebar-submenu-item--inactive'} w-full flex items-center space-x-2 rounded border px-3 py-1.5 text-left text-xs transition ${childActive ? 'border-blue-500/60 bg-blue-700/45 font-semibold text-blue-100 shadow-sm' : 'border-transparent text-slate-300 hover:bg-slate-600 hover:text-white'}`}
                          >
                            {child.icon && iconMap[child.icon]
                              ? React.createElement(iconMap[child.icon], { size: 14, className: modulePresentation[item.id]?.color || 'text-sky-400' })
                              : <Circle size={7} className={modulePresentation[item.id]?.color || 'text-sky-400'} fill="currentColor" />}
                            <span>{child.label[language] || child.label.FR}</span>
                          </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-4 space-y-2">
          {/* Version & Copyright */}
          {sidebarOpen && (
            <div className="text-xs text-slate-500 mt-4 pt-4 border-t border-slate-700 text-center">
              <p>M3S ERP v2.0</p>
              <p>© 2026 SENESWISS GROUP</p>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
        <Header onOpenMenu={() => setSidebarOpen(true)} />
        <main ref={mainRef} className="m3s-design-scope flex-1 overflow-auto">
          {returnPath && (
            <nav ref={returnNavRef} className="sticky top-0 z-30 border-b border-blue-800/70 bg-slate-950/95 px-3 py-2 shadow-sm backdrop-blur sm:px-5" aria-label={returnLabel}>
              <button
                type="button"
                onClick={() => navigate(returnPath)}
                className="inline-flex min-h-10 items-center gap-2 rounded-md border border-blue-600 bg-blue-950/70 px-3 text-sm font-semibold text-blue-100 transition hover:border-blue-400 hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <ArrowLeft size={17} aria-hidden="true" />
                {returnLabel}
              </button>
            </nav>
          )}
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
